import {
  Briefcase,
  FileText,
  Users,
  Headphones,
  Settings,
  LayoutGrid,
  ChevronDown,
  LogOut,
  Sparkles,
  Crown,
  Calendar,
  Send,
  Bell,
} from "lucide-react";
import { NavLink } from "@project/components/NavLink";
import { useLocation, useNavigate } from "react-router-dom";
import navigationRoutes from "@utils/navigationRoutes";
import { useAIAssistant } from "@project/hooks/useAIAssistant";
import { useAuth } from "@project/contexts/AuthContext";
import CandidateAuthService, { getCandidateAccessToken, getCandidateUserInfo } from "@project/services/CandidateAuthService";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@project/components/ui/sidebar";
import { cn } from "@project/lib/utils";
import hireflowLogo from "@project/assets/hireflow-logo.png";

const allNavItems = [
  {
    title: "Dashboard",
    url: navigationRoutes.ATS_DASHBOARD,
    icon: LayoutGrid,
    exact: true,
    badge: null,
    roles: ["admin", "hr", "interviewer", "candidate"],
  },
  {
    title: "Jobs",
    url: navigationRoutes.ATS_JOBS,
    icon: Briefcase,
    exact: false,
    badge: "8",
    roles: ["admin", "hr"],
  },
  {
    title: "Candidates",
    url: navigationRoutes.ATS_CANDIDATES,
    icon: Users,
    exact: false,
    badge: "12",
    roles: ["admin", "hr"],
  },
  {
    title: "Interviews",
    url: navigationRoutes.ATS_INTERVIEWS,
    icon: Calendar,
    exact: false,
    badge: "5",
    roles: ["admin", "hr", "interviewer"],
  },
  {
    title: "Offers",
    url: navigationRoutes.ATS_OFFERS,
    icon: Send,
    exact: false,
    badge: "3",
    roles: ["admin", "hr"],
  },
  {
    title: "Notifications",
    url: navigationRoutes.ATS_NOTIFICATIONS,
    icon: Bell,
    exact: true,
    badge: null,
    roles: ["admin", "hr", "interviewer", "candidate"],
  },
  {
    title: "Support",
    url: navigationRoutes.ATS_SUPPORT,
    icon: Headphones,
    exact: true,
    badge: null,
    roles: ["admin", "hr", "interviewer", "candidate"],
  },
  {
    title: "Settings",
    url: navigationRoutes.ATS_SETTINGS,
    icon: Settings,
    exact: true,
    badge: null,
    roles: ["admin", "hr", "interviewer", "candidate"],
  },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const { open: openAI } = useAIAssistant();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Real candidate login uses CandidateAuthService (not mock AuthContext)
  const candidateInfo = getCandidateUserInfo();
  const isRealCandidate = !!getCandidateAccessToken();
  const effectiveRole = user?.role || (isRealCandidate ? "candidate" : "admin");
  const effectiveName = isRealCandidate && candidateInfo
    ? `${candidateInfo.first_name} ${candidateInfo.last_name}`.trim()
    : user?.name || "User";
  const effectiveAvatar = isRealCandidate && candidateInfo
    ? candidateInfo.first_name?.[0]?.toUpperCase() ?? "C"
    : user?.avatar || "?";

  const mainItems = allNavItems.filter(item =>
    item.roles.includes(effectiveRole)
  );

  const isItemActive = (item: (typeof allNavItems)[0]) => {
    if (item.exact) {
      return location.pathname === item.url;
    }
    return (
      location.pathname === item.url ||
      location.pathname.startsWith(item.url + "/")
    );
  };

  const handleLogout = async () => {
    try {
      await CandidateAuthService.logout();
    } catch {
      // ignore errors, still redirect
    }
    logout();
    navigate(navigationRoutes.CANDIDATE_LOGIN);
  };

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      <SidebarContent className="relative flex flex-col overflow-hidden border-r border-sidebar-border bg-sidebar pb-3 pt-4 text-foreground">
        <div className="px-4 pb-4">
          {!collapsed ? (
            <div className="flex items-center gap-2.5">
              <img
                src={hireflowLogo}
                alt="HireFlow"
                className="h-9 w-auto rounded-lg object-contain"
              />
              <div>
                <h1 className="font-display text-[15px] font-bold leading-tight tracking-tight text-foreground">
                  HireFlow
                </h1>
                <p className="text-[10px] leading-tight text-muted-foreground">
                  AI-Powered ATS
                </p>
              </div>
            </div>
          ) : (
            <img
              src={hireflowLogo}
              alt="HireFlow"
              className="mx-auto h-9 w-9 rounded-lg object-contain"
            />
          )}
        </div>

        {!collapsed && (
          <div className="px-3 pb-3">
            <div className="flex w-full items-center gap-2.5 rounded-xl border border-sidebar-border bg-sidebar-accent/50 p-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary/30 to-primary-glow/20 text-[10px] font-bold text-foreground ring-1 ring-sidebar-border">
                {effectiveAvatar}
              </div>
              <div className="min-w-0 flex-1 text-left">
                <p className="truncate text-[12px] font-semibold leading-tight text-foreground">
                  {isRealCandidate ? effectiveName : "Universal Software"}
                </p>
                <p className="flex items-center gap-1 text-[10px] leading-tight text-muted-foreground">
                  <Crown className="h-2.5 w-2.5 text-warning" />
                  {isRealCandidate ? "Candidate" : "Enterprise"}
                </p>
              </div>
            </div>
          </div>
        )}

        {!collapsed && (
          <div className="px-5 pb-1 pt-1">
            <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
              Navigation
            </p>
          </div>
        )}

        <SidebarGroup className="flex-1">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-0.5 px-2.5">
              {mainItems.map(item => {
                const active = isItemActive(item);
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        end={item.exact}
                        className={cn(
                          "group/nav flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-[13px] font-medium transition-all duration-200",
                          active
                            ? "nav-item-active"
                            : "text-foreground/80 hover:bg-sidebar-accent/60 hover:text-foreground"
                        )}
                        activeClassName=""
                      >
                        <item.icon className="h-[16px] w-[16px] shrink-0" />
                        {!collapsed && (
                          <>
                            <span className="flex-1">{item.title}</span>
                            {item.badge && (
                              <span
                                className={cn(
                                  "min-w-[20px] rounded-md px-1.5 py-0.5 text-center text-[10px] font-bold",
                                  active
                                    ? "bg-primary/15 text-primary-foreground"
                                    : "bg-sidebar-accent text-muted-foreground"
                                )}
                              >
                                {item.badge}
                              </span>
                            )}
                          </>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {!collapsed && (
          <div className="px-3 pb-1 pt-2">
            <button
              onClick={openAI}
              className="group/ai flex w-full items-center gap-2 rounded-xl border border-primary/15 bg-gradient-to-r from-primary/15 to-primary-glow/10 p-3 transition-all hover:from-primary/20 hover:to-primary-glow/15"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-glow shadow-md shadow-primary/20">
                <Sparkles className="h-3.5 w-3.5 text-white" />
              </div>
              <div className="min-w-0 flex-1 text-left">
                <p className="text-[11px] font-semibold text-foreground">
                  AI Assistant
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Auto-screen & rank
                </p>
              </div>
            </button>
          </div>
        )}

        <div
          className={cn(
            "mt-1 border-t border-sidebar-border",
            collapsed ? "px-2 py-3" : "px-3 py-2.5"
          )}
        >
          {!collapsed ? (
            <button
              onClick={handleLogout}
              className="group/profile flex w-full items-center gap-2.5 rounded-xl p-2 transition-all hover:bg-sidebar-accent/50"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow ring-2 ring-primary/20">
                <span className="text-[11px] font-bold text-primary-foreground">
                  {effectiveAvatar}
                </span>
              </div>
              <div className="min-w-0 flex-1 text-left">
                <p className="truncate text-[12px] font-semibold leading-tight text-foreground">
                  {effectiveName}
                </p>
                <p className="text-[10px] capitalize text-muted-foreground">
                  {effectiveRole}
                </p>
              </div>
              <LogOut className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </button>
          ) : (
            <button
              onClick={handleLogout}
              className="mx-auto flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow ring-2 ring-primary/20"
            >
              <span className="text-[11px] font-bold text-primary-foreground">
                {effectiveAvatar}
              </span>
            </button>
          )}
        </div>
      </SidebarContent>
    </Sidebar>
  );
}
